import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isToday, isBefore, startOfDay } from "date-fns";
import { getDateLocale } from "@/lib/dateUtils";
import { Search, Hotel, Home, Sailboat, DoorOpen, Landmark, Container, TreePalm, SlidersHorizontal, ChevronDown, MapPin, X, Loader2, Users } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ApartmentIcon, ElegantCalendarIcon } from "@/components/icons/PropertyTypeIcons";

import { tAuto } from "@/lib/tAuto";
// Lista de províncias com Luanda em primeiro
const PROVINCES = [
  "Luanda", "Bengo", "Benguela", "Bié", "Cabinda", "Cuando", "Cuando Cubango",
  "Cuanza Norte", "Cuanza Sul", "Cunene", "Huambo", "Huíla",
  "Icolo e Bengo", "Lunda Norte", "Lunda Sul", "Malanje",
  "Moxico", "Moxico Leste", "Namibe", "Uíge", "Zaire"
];

// Tipos
interface GuestCounts {
  adults: number;
  children: number;
  babies: number;
}

// Property categories with colors matching the image
const PROPERTY_CATEGORIES = [
  { id: "hotel", label: "Hotel", icon: Hotel, color: "bg-blue-500", textColor: "text-blue-500", borderColor: "border-blue-500/20" },
  { id: "hospedaria", label: "Hospedaria", icon: Home, color: "bg-emerald-500", textColor: "text-emerald-500", borderColor: "border-emerald-500/20" },
  { id: "barco", label: "Barcos", icon: Sailboat, color: "bg-cyan-500", textColor: "text-cyan-500", borderColor: "border-cyan-500/20" },
  { id: "casa", label: "Casa", icon: Home, color: "bg-amber-500", textColor: "text-amber-500", borderColor: "border-amber-500/20" },
  { id: "apartamento", label: "Apartamento", icon: ApartmentIcon, color: "bg-primary", textColor: "text-primary", borderColor: "border-primary/20" },
  { id: "casa_hospedes", label: "Casa de hóspedes", icon: DoorOpen, color: "bg-rose-500", textColor: "text-rose-500", borderColor: "border-rose-500/20" },
  { id: "condominio", label: "Condomínio", icon: Landmark, color: "bg-primary", textColor: "text-primary", borderColor: "border-primary/20" },
  { id: "contentor", label: "Contentor", icon: Container, color: "bg-slate-500", textColor: "text-slate-500", borderColor: "border-slate-500/20" },
  { id: "resort", label: "Resort", icon: TreePalm, color: "bg-teal-500", textColor: "text-teal-500", borderColor: "border-teal-500/20" },
];

interface DesktopSearchBarProps {
  variant?: "guest" | "host";
  className?: string;
  onOpenFilters?: () => void;
}

export function DesktopSearchBar({ variant = "guest", className, onOpenFilters }: DesktopSearchBarProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // Estados de busca
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchLocation, setSearchLocation] = useState("");
  const [address, setAddress] = useState("");
  const [checkInDate, setCheckInDate] = useState<Date | null>(null);
  const [checkOutDate, setCheckOutDate] = useState<Date | null>(null);
  const [guests, setGuests] = useState<GuestCounts>({ adults: 2, children: 0, babies: 0 });
  
  // Estados de UI
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showGuestPicker, setShowGuestPicker] = useState(false);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const datePickerRef = useRef<HTMLDivElement>(null);
  const guestPickerRef = useRef<HTMLDivElement>(null);
  const addressRef = useRef<HTMLDivElement>(null);
  
  // Temp states for date picker
  const [tempDates, setTempDates] = useState<{ checkIn: Date | null; checkOut: Date | null }>({
    checkIn: null,
    checkOut: null
  });

  const totalGuests = guests.adults + guests.children + guests.babies;

  // Busca em tempo real de endereços/espaços
  const fetchSearchSuggestions = useCallback(async (query: string) => {
    if (!query || query.length < 2) {
      setSearchSuggestions([]);
      return;
    }
    
    setIsSearching(true);
    try {
      const { data, error } = await supabase
        .from('properties')
        .select('id, title, address, city, province, property_type')
        .or(`title.ilike.%${query}%,address.ilike.%${query}%,city.ilike.%${query}%`)
        .limit(6);
      
      if (error) throw error;
      setSearchSuggestions(data || []);
    } catch (error) {
      console.error('Erro na busca:', error);
      setSearchSuggestions([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounce para busca
  const handleAddressChange = (value: string) => {
    setAddress(value);
    setShowAddressSuggestions(true);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      fetchSearchSuggestions(value);
    }, 300);
  };

  // Selecionar sugestão
  const selectSuggestion = (property: any) => {
    setAddress(property.address || property.title);
    setSearchLocation(property.province || "");
    setSearchSuggestions([]);
    setShowAddressSuggestions(false);
  };

  // Cleanup
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Click outside handlers
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setShowDatePicker(false);
      }
      if (guestPickerRef.current && !guestPickerRef.current.contains(event.target as Node)) {
        setShowGuestPicker(false);
      }
      if (addressRef.current && !addressRef.current.contains(event.target as Node)) {
        setShowAddressSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (searchLocation) params.set("location", searchLocation);
    if (address) params.set("address", address);
    if (selectedCategory) params.set("type", selectedCategory);
    if (totalGuests > 0) params.set("guests", totalGuests.toString());
    if (checkInDate) params.set("checkIn", checkInDate.toISOString().split('T')[0]);
    if (checkOutDate) params.set("checkOut", checkOutDate.toISOString().split('T')[0]);
    
    if (variant === "host") {
      navigate(`/dashboard?${params.toString()}`);
    } else {
      navigate(`/explorar?${params.toString()}`);
    }
  };

  const handleCategoryClick = (categoryId: string) => {
    setSelectedCategory(selectedCategory === categoryId ? null : categoryId);
  };

  // Date picker helpers
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  const handleDateClick = (date: Date) => {
    if (!tempDates.checkIn || (tempDates.checkIn && tempDates.checkOut)) {
      setTempDates({ checkIn: date, checkOut: null });
    } else if (tempDates.checkIn && !tempDates.checkOut) {
      if (isBefore(date, tempDates.checkIn)) {
        setTempDates({ checkIn: date, checkOut: tempDates.checkIn });
      } else {
        setTempDates({ ...tempDates, checkOut: date });
      }
    }
  };

  const applyDates = () => {
    if (tempDates.checkIn && tempDates.checkOut) {
      setCheckInDate(tempDates.checkIn);
      setCheckOutDate(tempDates.checkOut);
      setShowDatePicker(false);
    } else {
      toast.info("Selecione check-in e check-out");
    }
  };

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const weekDays = [t('search.sun'), t('search.mon'), t('search.tue'), t('search.wed'), t('search.thu'), t('search.fri'), t('search.sat')];

  return (
    <div className={cn("w-full max-w-5xl mx-auto", className)}>
      {/* Search Bar - Different layouts for guest vs host */}
      <div className="hidden md:block">
        {variant === "host" ? (
          // HOST VERSION: ONDE (input com busca em tempo real) + Search + Filtro
          <div className="relative flex items-center bg-white rounded-full shadow-lg border border-gray-200 hover:shadow-xl transition-shadow duration-300 max-w-2xl mx-auto">
            {/* ONDE? - Input com busca em tempo real */}
            <div ref={addressRef} className="flex-1 px-5 py-3 text-left rounded-l-full relative group hover:bg-gray-50 transition-colors">
              <div className="text-[10px] font-bold text-[#E8513D]/80 uppercase tracking-wider mb-0.5">Onde?</div>
              <div className="relative">
                <input
                  type="text"
                  value={address}
                  onChange={(e) => handleAddressChange(e.target.value)}
                  placeholder={tAuto("auto.components_search_desktopsearchbar.1", "Digite cidade, província ou endereço")}
                  className="text-sm text-gray-700 bg-transparent border-none outline-none w-full pr-6 placeholder:text-gray-400"
                />
                {address && (
                  <button
                    onClick={() => { setAddress(""); setSearchSuggestions([]); }}
                    className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center"
                  >
                    <X className="w-3 h-3 text-gray-500" />
                  </button>
                )}
              </div>
              
              {/* Sugestões de endereço */}
              {showAddressSuggestions && (searchSuggestions.length > 0 || isSearching) && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50 min-w-[300px]">
                  {isSearching ? (
                    <div className="px-4 py-3 flex items-center gap-2 text-gray-500">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm">{t('search.searching')}</span>
                    </div>
                  ) : (
                    <>
                      <p className="px-4 py-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{t('search.suggestions')}</p>
                      {searchSuggestions.map((property) => (
                        <button
                          key={property.id}
                          onClick={() => selectSuggestion(property)}
                          className="w-full px-4 py-2.5 text-left hover:bg-gray-50 flex items-start gap-3 transition-colors"
                        >
                          <MapPin className="w-4 h-4 text-[#E8513D] mt-0.5 shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">{property.title || property.address}</p>
                            <p className="text-xs text-gray-500">{property.city}, {property.province}</p>
                          </div>
                        </button>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="w-px h-8 bg-gray-200" />
            
            {/* Botões Pesquisar + Filtro */}
            <div className="flex items-center gap-2 pr-2">
              {/* Botão Pesquisar - Elegante */}
              <button 
                onClick={handleSearch}
                className="bg-[#E8513D] hover:bg-[#D14430] text-white rounded-full px-5 py-2.5 flex items-center gap-2 transition-all duration-200 shadow-md hover:shadow-lg hover:scale-105 active:scale-95"
              >
                <Search className="w-4 h-4" />
                <span className="font-semibold text-sm">{t('hero.search.searchButton')}</span>
              </button>
              
              {/* Botão Filtro - Elegante */}
              <button 
                onClick={onOpenFilters}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full px-4 py-2.5 flex items-center gap-2 transition-all duration-200 border border-gray-300 hover:border-gray-400 hover:scale-105 active:scale-95"
                title={t('search.advancedFilters')}
              >
                <SlidersHorizontal className="w-4 h-4" />
                <span className="font-semibold text-sm">{t('search.filter')}</span>
              </button>
            </div>
          </div>
        ) : (
          // GUEST VERSION: ENDEREÇO + DATAS + HÓSPEDES + PROVÍNCIA + Pesquisar + Filtro
          <div className="relative">
            <div className="flex items-center bg-white rounded-full shadow-lg border border-gray-200/80 hover:shadow-xl transition-shadow duration-300 max-w-5xl mx-auto px-4 py-2 gap-2">
              {/* ENDEREÇO - Input com busca em tempo real */}
              <div ref={addressRef} className="flex-[1.2] min-w-[180px] flex items-center gap-3 px-3 py-1.5 rounded-full hover:bg-gray-50 transition-colors relative group">
                <MapPin className="w-5 h-5 text-[#E8513D] shrink-0" />
                <div className="flex-1 min-w-0 text-left">
                  <div className="text-[10px] font-bold text-[#E8513D] uppercase tracking-wider mb-0.5">{t('hero.search.where')}</div>
                  <div className="relative">
                    <input
                      type="text"
                      value={address}
                      onChange={(e) => handleAddressChange(e.target.value)}
                      placeholder={t('search.addressPlaceholder')}
                      className="text-sm text-gray-700 bg-transparent border-none outline-none w-full pr-6 placeholder:text-gray-400 font-medium"
                    />
                    {address && (
                      <button
                        onClick={() => { setAddress(""); setSearchSuggestions([]); }}
                        className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center"
                      >
                        <X className="w-3 h-3 text-gray-500" />
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Sugestões de endereço */}
                {showAddressSuggestions && (searchSuggestions.length > 0 || isSearching) && (
                  <div className="absolute top-full left-0 right-0 mt-3 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50 min-w-[300px]">
                    {isSearching ? (
                      <div className="px-4 py-3 flex items-center gap-2 text-gray-500">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="text-sm">{t('search.searching')}</span>
                      </div>
                    ) : (
                      <>
                        <p className="px-4 py-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{t('search.suggestions')}</p>
                        {searchSuggestions.map((property) => (
                          <button
                            key={property.id}
                            onClick={() => selectSuggestion(property)}
                            className="w-full px-4 py-2.5 text-left hover:bg-gray-50 flex items-start gap-3 transition-colors"
                          >
                            <MapPin className="w-4 h-4 text-[#E8513D] mt-0.5 shrink-0" />
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-800 truncate">{property.title || property.address}</p>
                              <p className="text-xs text-gray-500">{property.city}, {property.province}</p>
                            </div>
                          </button>
                        ))}
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Divider */}
              <div className="w-px h-8 bg-zinc-200/80 shrink-0" />
              
              {/* DATAS - Check-in/Check-out */}
              <div ref={datePickerRef} className="relative flex-1 min-w-[180px]">
                <button 
                  className="flex items-center gap-3 px-3 py-1.5 rounded-full hover:bg-gray-50 transition-colors text-left w-full"
                  onClick={() => {
                    setShowDatePicker(!showDatePicker);
                    setShowGuestPicker(false);
                    if (!showDatePicker) {
                      setTempDates({ checkIn: checkInDate, checkOut: checkOutDate });
                    }
                  }}
                >
                  <ElegantCalendarIcon className="w-5 h-5 text-black shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-bold text-[#E8513D] uppercase tracking-wider mb-0.5">{t('hero.search.when')}</div>
                    <div className="text-sm text-gray-700 font-medium whitespace-nowrap w-full">
                      {checkInDate && checkOutDate ? (
                        <span>{format(checkInDate, "dd MMM", { locale: getDateLocale() })} - {format(checkOutDate, "dd MMM", { locale: getDateLocale() })}</span>
                      ) : (
                        <span className="text-gray-400 font-normal">{t('hero.search.whenPlaceholder')}</span>
                      )}
                    </div>
                  </div>
                </button>
                
                {/* Date Picker Dropdown */}
                {showDatePicker && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 bg-white rounded-2xl shadow-2xl border border-gray-100 p-4 z-50 w-[340px]">
                    {/* Header com navegação */}
                    <div className="flex items-center justify-between mb-4">
                      <button
                        onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                        className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors"
                      >
                        <ChevronDown className="w-4 h-4 rotate-90 text-gray-600" />
                      </button>
                      <h4 className="font-semibold text-gray-800 capitalize">
                        {format(currentMonth, "MMMM yyyy", { locale: getDateLocale() })}
                      </h4>
                      <button
                        onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                        className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors"
                      >
                        <ChevronDown className="w-4 h-4 -rotate-90 text-gray-600" />
                      </button>
                    </div>
                    
                    {/* Week days header */}
                    <div className="grid grid-cols-7 gap-1 mb-2">
                      {weekDays.map((day) => (
                        <div key={day} className="text-center text-[10px] font-semibold text-gray-400 py-1">
                          {day}
                        </div>
                      ))}
                    </div>
                    
                    {/* Calendar grid */}
                    <div className="grid grid-cols-7 gap-1">
                      {days.map((date) => {
                        const isSelected = (tempDates.checkIn && isSameDay(date, tempDates.checkIn)) || 
                                          (tempDates.checkOut && isSameDay(date, tempDates.checkOut));
                        const isInRange = tempDates.checkIn && tempDates.checkOut && 
                                          date > tempDates.checkIn && date < tempDates.checkOut;
                        const isDisabled = isBefore(date, startOfDay(new Date()));
                        
                        return (
                          <button
                            key={date.toISOString()}
                            onClick={() => handleDateClick(date)}
                            disabled={isDisabled}
                            className={cn(
                              "h-9 w-full rounded-lg text-sm font-medium transition-all",
                              isSelected && "bg-[#E8513D] text-white",
                              isInRange && "bg-[#E8513D]/10 text-[#E8513D]",
                              !isSelected && !isInRange && !isDisabled && "hover:bg-gray-100 text-gray-700",
                              isDisabled && "opacity-30 cursor-not-allowed text-gray-400",
                              isToday(date) && !isSelected && "ring-1 ring-[#E8513D]/30"
                            )}
                          >
                            {format(date, "d")}
                          </button>
                        );
                      })}
                    </div>
                    
                    {/* Seleção atual */}
                    <div className="mt-4 pt-3 border-t border-gray-100">
                      <div className="flex items-center justify-between text-sm">
                        <div>
                          <span className="text-gray-500">Check-in:</span>
                          <span className="ml-2 font-medium">
                            {tempDates.checkIn ? format(tempDates.checkIn, "dd/MM/yyyy") : "—"}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">Check-out:</span>
                          <span className="ml-2 font-medium">
                            {tempDates.checkOut ? format(tempDates.checkOut, "dd/MM/yyyy") : "—"}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Botões de ação */}
                    <div className="flex gap-2 mt-4">
                      <button
                        onClick={() => setShowDatePicker(false)}
                        className="flex-1 py-2 rounded-lg border border-gray-200 text-gray-600 font-medium hover:bg-gray-50 transition-colors"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={applyDates}
                        disabled={!tempDates.checkIn || !tempDates.checkOut}
                        className={cn(
                          "flex-1 py-2 rounded-lg font-medium transition-colors",
                          tempDates.checkIn && tempDates.checkOut
                            ? "bg-[#E8513D] text-white hover:bg-[#D14430]"
                            : "bg-gray-100 text-gray-400 cursor-not-allowed"
                        )}
                      >
                        {t('search.applyDates')}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Divider */}
              <div className="w-px h-8 bg-zinc-200/80 shrink-0" />
              
              {/* HÓSPEDES */}
              <div ref={guestPickerRef} className="relative flex-1 min-w-[160px]">
                <button 
                  className="flex items-center gap-3 px-3 py-1.5 rounded-full hover:bg-gray-50 transition-colors text-left w-full"
                  onClick={() => {
                    setShowGuestPicker(!showGuestPicker);
                    setShowDatePicker(false);
                  }}
                >
                  <Users className="w-5 h-5 text-[#E8513D] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-bold text-[#E8513D] uppercase tracking-wider mb-0.5">{t('hero.search.guests')}</div>
                    <div className="text-sm text-gray-700 font-medium whitespace-nowrap flex items-center justify-between gap-1 w-full">
                      <span >
                        {totalGuests > 0 ? (
                          `${totalGuests} ${
                            (totalGuests > 1 ? t('search.guestsPlural') : t('search.guest'))
                              .replace(/^\w/, (c) => c.toUpperCase())
                          }`
                        ) : (
                          t('search.selectDates')
                        )}
                      </span>
                      <ChevronDown className="w-4 h-4 text-gray-500 shrink-0" />
                    </div>
                  </div>
                </button>
                
                {/* Guest Picker Dropdown */}
                {showGuestPicker && (
                  <div className="absolute top-full right-0 mt-3 bg-white rounded-2xl shadow-2xl border border-gray-100 p-4 z-50 w-[280px]">
                    {/* Adults */}
                    <div className="flex items-center justify-between py-3 border-b border-gray-100">
                      <div>
                        <p className="font-medium text-gray-800">{t('search.adults')}</p>
                        <p className="text-xs text-gray-500">{t('search.adultsAge')}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setGuests(prev => ({ ...prev, adults: Math.max(0, prev.adults - 1) }))}
                          className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:border-gray-400 transition-colors disabled:opacity-50"
                          disabled={guests.adults <= 0}
                        >
                          <span className="text-lg leading-none">−</span>
                        </button>
                        <span className="w-6 text-center font-medium">{guests.adults}</span>
                        <button
                          onClick={() => setGuests(prev => ({ ...prev, adults: prev.adults + 1 }))}
                          className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:border-gray-400 transition-colors"
                        >
                          <span className="text-lg leading-none">+</span>
                        </button>
                      </div>
                    </div>
                    
                    {/* Children */}
                    <div className="flex items-center justify-between py-3 border-b border-gray-100">
                      <div>
                        <p className="font-medium text-gray-800">{t('search.children')}</p>
                        <p className="text-xs text-gray-500">{t('search.childrenAge')}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setGuests(prev => ({ ...prev, children: Math.max(0, prev.children - 1) }))}
                          className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:border-gray-400 transition-colors disabled:opacity-50"
                          disabled={guests.children <= 0}
                        >
                          <span className="text-lg leading-none">−</span>
                        </button>
                        <span className="w-6 text-center font-medium">{guests.children}</span>
                        <button
                          onClick={() => setGuests(prev => ({ ...prev, children: prev.children + 1 }))}
                          className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:border-gray-400 transition-colors"
                        >
                          <span className="text-lg leading-none">+</span>
                        </button>
                      </div>
                    </div>
                    
                    {/* Babies */}
                    <div className="flex items-center justify-between py-3">
                      <div>
                        <p className="font-medium text-gray-800">{t('search.babies')}</p>
                        <p className="text-xs text-gray-500">{t('search.babiesAge')}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setGuests(prev => ({ ...prev, babies: Math.max(0, prev.babies - 1) }))}
                          className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:border-gray-400 transition-colors disabled:opacity-50"
                          disabled={guests.babies <= 0}
                        >
                          <span className="text-lg leading-none">−</span>
                        </button>
                        <span className="w-6 text-center font-medium">{guests.babies}</span>
                        <button
                          onClick={() => setGuests(prev => ({ ...prev, babies: prev.babies + 1 }))}
                          className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:border-gray-400 transition-colors"
                        >
                          <span className="text-lg leading-none">+</span>
                        </button>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => setShowGuestPicker(false)}
                      className="w-full mt-3 py-2.5 rounded-lg bg-[#E8513D] text-white font-medium hover:bg-[#D14430] transition-colors"
                    >
                      {t('search.apply')}
                    </button>
                  </div>
                )}
              </div>
              
              {/* Divider */}
              <div className="w-px h-8 bg-zinc-200/80 shrink-0" />
              
              {/* PROVÍNCIA - Dropdown compacto */}
              <div className="px-3 py-1.5 flex-1 relative flex flex-col justify-center min-w-[180px] group hover:bg-gray-50 rounded-full transition-colors cursor-pointer">
                <div className="text-[10px] font-bold text-[#E8513D] uppercase tracking-wider mb-0.5">{t('search.province')}</div>
                <div className="relative flex items-center">
                  <select
                    value={searchLocation}
                    onChange={(e) => setSearchLocation(e.target.value)}
                    className="text-sm font-medium text-gray-700 bg-transparent border-none outline-none w-full appearance-none pr-6 cursor-pointer"
                  >
                    <option value="">{t('search.allProvinces', 'Todas as Províncias')}</option>
                    {PROVINCES.map((province) => (
                      <option key={province} value={province}>{province}</option>
                    ))}
                  </select>
                  <ChevronDown className="w-4 h-4 text-gray-500 absolute right-0 pointer-events-none" />
                </div>
              </div>
              
              {/* Botões Pesquisar e Filtro Avançado contidos na barra */}
              <div className="flex items-center gap-2 pl-2 shrink-0">
                <button 
                  onClick={handleSearch}
                  className="bg-[#E8513D] hover:bg-[#D14430] text-white rounded-full px-5 py-2.5 flex items-center gap-2 font-semibold shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all duration-200 shrink-0"
                >
                  <Search className="w-4 h-4" />
                  <span className="text-sm">{t('hero.search.searchButton')}</span>
                </button>

                <button 
                  onClick={onOpenFilters}
                  title={t('search.advancedFilters')}
                  className="w-10 h-10 bg-gray-50 hover:bg-gray-100 text-gray-600 rounded-full flex items-center justify-center transition-colors border border-gray-200/60 shrink-0"
                >
                  <SlidersHorizontal className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Property Type Filters - Only for GUEST variant */}
      {variant === "guest" && (
        <div className="hidden md:flex flex-wrap items-center justify-center gap-2 mt-6">
          {PROPERTY_CATEGORIES.map((category) => {
            const Icon = category.icon;
            const isSelected = selectedCategory === category.id;
            
            return (
              <button
                key={category.id}
                onClick={() => handleCategoryClick(category.id)}
                className={cn(
                  "flex items-center gap-2 px-3 py-2 rounded-full border-2 transition-all duration-200 hover:scale-105 active:scale-95",
                  isSelected 
                    ? cn("bg-white", category.borderColor, category.textColor, "shadow-md")
                    : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
                )}
              >
                <div className={cn(
                  "w-6 h-6 rounded-full flex items-center justify-center text-white shrink-0",
                  category.color
                )}>
                  <Icon className="w-3.5 h-3.5" />
                </div>
                <span className="text-xs font-medium whitespace-nowrap">{category.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
